/**
 * Cobrança do WhatsApp — regras puras, sem I/O.
 *
 * Contexto que explica o formato dos dados: desde 1º de julho de 2025 a Meta
 * cobra POR MENSAGEM de template, por categoria (marketing, utilidade,
 * autenticação), e conversas de serviço (resposta dentro da janela de 24h)
 * são gratuitas. O endpoint `pricing_analytics` devolve, por dia, o par
 * (mensagens, custo) quebrado por categoria e por tipo de preço.
 *
 * Duas armadilhas moram aqui, e as duas já custaram número errado em tela:
 *
 *  1. `cost` ausente NÃO é zero. A Meta omite custo quando o WABA é faturado
 *     por Parceiro de Solução. Zero significa "não custou"; ausente significa
 *     "não sei". Somar ausente como zero mostra um mês de graça para quem
 *     está pagando — por isso `custoConhecido` anda junto do total.
 *  2. O valor vem na moeda do WABA, não na do workspace. Formatar com a moeda
 *     errada erra por ~5x numa conta em dólar.
 */

/** Categorias de preço que a Meta devolve. UNKNOWN cobre o que for novo. */
export const CATEGORIAS_PRECO = [
  "MARKETING",
  "UTILITY",
  "AUTHENTICATION",
  "AUTHENTICATION_INTERNATIONAL",
  "SERVICE",
  "UNKNOWN",
] as const;
export type CategoriaPreco = (typeof CATEGORIAS_PRECO)[number];

export const ROTULO_CATEGORIA: Record<string, string> = {
  MARKETING: "Marketing",
  UTILITY: "Utilidade",
  AUTHENTICATION: "Autenticação",
  AUTHENTICATION_INTERNATIONAL: "Autenticação internacional",
  SERVICE: "Atendimento",
  UNKNOWN: "Outras",
};

/** Tipos de preço. Os dois FREE_* não geram cobrança. */
export type TipoPreco = "REGULAR" | "FREE_CUSTOMER_SERVICE" | "FREE_ENTRY_POINT" | "UNKNOWN";

export type ModoCobranca = "meta_direct" | "reseller";

/** Uma linha crua, como sai da tabela `whatsapp_usage_daily`. */
export interface LinhaUso {
  date: string;
  category: string;
  pricingType: string;
  messages: number;
  /** Custo na moeda do WABA. `null` = a Meta não informou. */
  cost: number | null;
}

export interface ConfigCobranca {
  modo: ModoCobranca;
  /** Margem percentual sobre o custo. Só vale em `reseller`. */
  markupPercent: number;
  /** Preço por mensagem por categoria, para quando a Meta não informa custo. */
  rateOverrides?: Record<string, number> | null;
}

export interface ValorCalculado {
  /** Soma do que a Meta informou. Não inclui linha sem custo. */
  custoMeta: number;
  /** Mensagens cujo custo a Meta não informou (e não foi possível estimar). */
  mensagensSemCusto: number;
  /** Custo estimado por tabela de preço própria, para as linhas sem custo. */
  custoEstimado: number;
  /** O que o cliente paga: (custoMeta + custoEstimado) × (1 + margem). */
  total: number;
  /** false quando sobrou mensagem sem custo e sem preço configurado. */
  custoConhecido: boolean;
}

const FREE_TYPES = new Set(["FREE_CUSTOMER_SERVICE", "FREE_ENTRY_POINT"]);

/** Uma linha grátis não é uma linha sem custo: ela custa zero, e sabemos disso. */
export function ehGratuita(pricingType: string): boolean {
  return FREE_TYPES.has(pricingType);
}

/**
 * Calcula o valor de um conjunto de linhas segundo a config do workspace.
 *
 * Em `meta_direct` a margem é ignorada mesmo se estiver configurada: o cliente
 * paga a Meta direto, então inventar margem aqui mostraria um valor que
 * ninguém vai cobrar dele.
 */
export function calcularValor(linhas: LinhaUso[], config: ConfigCobranca): ValorCalculado {
  let custoMeta = 0;
  let custoEstimado = 0;
  let mensagensSemCusto = 0;

  for (const linha of linhas) {
    if (linha.cost != null) {
      custoMeta += linha.cost;
      continue;
    }
    if (ehGratuita(linha.pricingType)) continue; // grátis: custo zero conhecido

    const preco = config.rateOverrides?.[linha.category];
    if (typeof preco === "number" && Number.isFinite(preco)) {
      custoEstimado += preco * linha.messages;
    } else {
      mensagensSemCusto += linha.messages;
    }
  }

  const base = custoMeta + custoEstimado;
  const margem = config.modo === "reseller" ? Math.max(0, config.markupPercent) / 100 : 0;

  return {
    custoMeta: arredonda(custoMeta),
    custoEstimado: arredonda(custoEstimado),
    mensagensSemCusto,
    total: arredonda(base * (1 + margem)),
    custoConhecido: mensagensSemCusto === 0,
  };
}

/** Duas casas é o que se cobra; seis casas acumuladas viram centavo fantasma. */
function arredonda(v: number): number {
  return Math.round(v * 100) / 100;
}

/**
 * Projeção de fechamento do mês: média dos dias COMPLETOS, multiplicada pelo
 * tamanho do mês. Deliberadamente burra — média simples, sem sazonalidade.
 * Uma projeção esperta que erra é pior que uma projeção óbvia que erra, porque
 * ninguém consegue explicar a diferença para o cliente.
 *
 * `gastoAteOntem` é o que já foi gasto SEM o dia corrente, e o nome é assim
 * justamente porque a versão anterior dividia o total (com o dia de hoje pela
 * metade) pelos dias completos e projetava a mais todo dia.
 *
 * Só projeta a partir do dia 2: no dia 1º não há dia completo, e a média de um
 * dia parcial multiplicada por 30 é ruído, não previsão.
 */
export function projetarMes(
  gastoAteOntem: number,
  diaDoMes: number,
  diasNoMes: number,
): number | null {
  if (diaDoMes < 2 || diasNoMes < diaDoMes) return null;
  const diasCompletos = diaDoMes - 1;
  if (gastoAteOntem <= 0) return 0;
  return arredonda((gastoAteOntem / diasCompletos) * diasNoMes);
}

/** Formata na moeda do WABA. `pt-BR` porque o painel é em português. */
export function formatarMoeda(valor: number, moeda?: string | null): string {
  const currency = moeda && /^[A-Z]{3}$/.test(moeda) ? moeda : "BRL";
  try {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency }).format(valor);
  } catch {
    return `${currency} ${valor.toFixed(2)}`;
  }
}
